<template>
  <div class="refund-deposit-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('refundDepositFee.title')" :subtitle="$t('refundDepositFee.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-printer" @click="printRefundFeeReceipt">
          {{ $t('refundDepositFee.printReceipt') }}
        </el-button>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 状态筛选：语义标签 + 左侧状态列表 -->
    <div class="refund-deposit-fee-layout">
      <div class="ds-panel ds-panel--state">
        <div class="ds-state-list">
          <button v-for="(item, index) in refundDepositFeeInfo.states" :key="index" type="button"
            class="ds-state-list__item" :class="{ 'is-active': refundDepositFeeInfo.state === item.state }"
            @click="switchDepositState(item)">
            {{ item.name }}
          </button>
        </div>
      </div>

      <div class="refund-deposit-fee-main">
        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="listFeeDeposit">
            <template #left>
              <span class="ds-panel__title">{{ $t('refundDepositFee.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="refundDepositFeeInfo.fees" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('refundDepositFee.page.empty')" @retry="listFeeDeposit">
            <el-table-column prop="payerObjName" :label="$t('payFeeDeposit.payerObject')" align="left" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.payerObjName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeName" :label="$t('payFeeDeposit.feeItem')" align="left" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.feeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('payFeeDeposit.timePeriod')" align="center" width="200">
              <template slot-scope="scope">
                <div class="ds-time-cell">
                  <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
                  <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="receivedAmount" :label="$t('payFeeDeposit.amount')" align="right" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.receivedAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('payFeeDeposit.paymentTime')" align="center" width="180">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('payFeeDeposit.status')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('payFeeDeposit.operation')" align="center" width="170" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="scope.row.state === '1400'" size="mini" type="text" @click="openRefundModel(scope.row)">
                  {{ $t('payFeeDeposit.refundDeposit') }}
                </el-button>
                <el-button size="mini" type="text" @click="toFeeDetail(scope.row)">
                  {{ $t('payFeeDeposit.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('refundDepositFee.page.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </div>
    </div>

    <return-pay-fee ref="returnPayFee" @success="handleSuccess" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { queryFeeDeposit } from '@/api/fee/refundDepositFeeApi'
import ReturnPayFee from '@/components/fee/returnPayFee'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'RefundDepositFeeList',
  components: {
    ReturnPayFee,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      refundDepositFeeInfo: {
        fees: [],
        states: [
          { name: this.$t('refundDepositFee.unrefunded'), state: '1400' },
          { name: this.$t('refundDepositFee.refunded'), state: '1100' },
          { name: this.$t('refundDepositFee.pendingReview'), state: '1000' }
        ],
        state: '1400',
        roomId: '',
        ownerId: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.refundDepositFeeInfo.roomId = this.$route.query.roomId
    this.refundDepositFeeInfo.ownerId = this.$route.query.ownerId
    this.listFeeDeposit()
  },
  methods: {
    async listFeeDeposit() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          payerObjId: this.refundDepositFeeInfo.roomId,
          ownerId: this.refundDepositFeeInfo.ownerId,
          communityId: this.communityId,
          state: this.refundDepositFeeInfo.state
        }
        const { data, total } = await queryFeeDeposit(params)
        this.refundDepositFeeInfo.fees = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取押金费用列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('refundDepositFee.page.loadFailed')
        this.$message.error(this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    switchDepositState(state) {
      this.refundDepositFeeInfo.state = state.state
      this.pagination.current = 1
      this.listFeeDeposit()
    },
    toFeeDetail(fee) {
      this.$router.push(`/views/fee/propertyFee?feeId=${fee.feeId}`)
    },
    printRefundFeeReceipt() {
      window.open(`/#/views/owner/ownerDetail?ownerId=${this.refundDepositFeeInfo.ownerId}&currentTab=ownerDetailReceipt`)
    },
    openRefundModel(fee) {
      const feeData = {
        ...fee,
        mainFeeInfo: {
          feeId: fee.feeId,
          feeTypeCd: fee.feeTypeCd,
          configId: fee.configId
        },
        cycles: 1,
        receivableAmount: fee.receivedAmount,
        communityId: this.communityId
      }
      this.$refs.returnPayFee.open(feeData)
    },
    handleSuccess() {
      this.listFeeDeposit()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.listFeeDeposit()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.listFeeDeposit()
    },
    goBack() {
      this.$router.go(-1)
    },
    /**
     * 押金退费状态：1400 未退款 / 1100 已退款 / 1000 待审核
     */
    resolveState(row) {
      if (!row) return 'muted'
      const map = {
        1400: 'warning',
        1100: 'success',
        1000: 'primary'
      }
      const key = row.state === null || row.state === undefined ? '' : String(row.state)
      if (map[key]) return map[key]
      const resolved = resolveStatus(row.state, {
        text: row.stateName || '',
        fallbackLabel: row.stateName || ''
      })
      return resolved.type
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.refund-deposit-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧状态列表 + 右侧主表格 */
.refund-deposit-fee-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.refund-deposit-fee-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 状态列表面板 */
.ds-panel--state {
  width: 200px;
  flex-shrink: 0;
  padding: var(--ds-spacing-sm);
}

/* 状态列表项 */
.ds-state-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-spacing-xs);

  &__item {
    display: block;
    width: 100%;
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border: none;
    border-radius: var(--ds-radius-sm);
    background: transparent;
    color: var(--ds-gray-700);
    font-size: var(--ds-font-size-sm);
    text-align: center;
    cursor: pointer;
    transition: all var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 起止时间上下两行 */
.ds-time-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.6;

  .ds-num {
    font-size: var(--ds-font-size-xs);
  }
}
</style>
